import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import '../style.css';
import Register from './Register';
import { FlagIcon } from 'react-flag-kit';
import TestimonialsSection from './components/Testimonials';
import {
  fadeUp,
  fadeLeft,
  staggerContainer,
  scaleFade,
  viewport,
} from '../components/useScrollAnimation';

const programs = [
  {
    flag: 'USA',
    code: "US",
    country: 'United States of America',
    badge: 'J1 Internship Program',
    title: 'Internship & Training di Hotel Bintang 5 Amerika',
    desc: 'Raih pengalaman kerja profesional di hotel mewah kelas dunia di Amerika Serikat, sekaligus bangun karier internasional yang gemilang.',
    image: '/img/j1-card.webp',
    highlights: [
      { icon: 'fa-solid fa-dollar-sign', text: 'Allowance $14.81–$19/jam (±Rp 514 juta/tahun)' },
      { icon: 'fa-solid fa-hotel', text: 'Partner hotel bergengsi seperti St. Regis, Marriott & lebih' },
      { icon: 'fa-solid fa-clock', text: 'Program 6–18 bulan, termasuk akomodasi & visa J1' },
    ],
    cta: 'Lihat Program J1',
    ctaSecondary: 'Daftar Sekarang',
    link: '/j1-internship',
    theme: 'hp-card-red',
    accent: '#E31E28',
    accentSoft: 'rgba(227,30,40,0.08)',
  },
  {
    flag: 'Australia',
    code: "AU",
    country: 'Australia',
    badge: 'Study Abroad Program',
    title: 'Kuliah + Paid Internship di The Hotel School Australia',
    desc: 'Gabungkan pengalaman akademik di kampus perhotelan bergengsi Southern Cross University dengan magang berbayar di industri pariwisata kelas dunia.',
    image: '/img/study-abroad-card.webp',
    highlights: [
      { icon: 'fa-solid fa-dollar-sign', text: 'Upah magang ± AUD 27/jam — double income opportunity' },
      { icon: 'fa-solid fa-graduation-cap', text: 'Credit transfer SKS ke universitas asal di Indonesia' },
      { icon: 'fa-solid fa-trophy', text: '97% lulusan bekerja dalam 6 bulan setelah kelulusan' },
    ],
    cta: 'Lihat Program Study Abroad',
    ctaSecondary: 'Konsultasi Gratis',
    link: '/study-abroad',
    theme: 'hp-card-red',
    accent: '#1a3a6b',
    accentSoft: 'rgba(26,58,107,0.07)',
  },
  {
    flag: 'Australia',
    code: "AU",
    country: 'Australia',
    badge: 'Working Holiday Visa',
    title: 'Kerja & Liburan di Australia dengan WHV',
    desc: 'Tinggal, liburan, sekaligus mendapatkan pengalaman kerja di Australia. Kami dampingi dari persiapan dokumen hingga keberangkatan.',
    image: '/img/whv.jpeg',
    highlights: [
      { icon: 'fa-solid fa-passport', text: 'Terbuka untuk WNI usia 18–30 tahun' },
      { icon: 'fa-solid fa-language', text: 'Bimbingan intensif IELTS & persiapan Functional English' },
      { icon: 'fa-solid fa-briefcase', text: 'CV standar Australia, Job Interview & Pre-Departure Preparation' },
    ],
    cta: 'Lihat Program WHV',
    ctaSecondary: 'Konsultasi Gratis',
    link: '/working-holiday-visa',
    theme: 'hp-card-red',
    accent: '#dc3545',
    accentSoft: 'rgba(220,53,69,0.08)',
  },
];

const HomePreview = () => {

  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash]);

  return (
    <section className="hp-programs-section">
      <div className="container">

        {/* Section header — fade up */}
        <motion.div
          className="hp-section-header"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
        >
          <span className="hp-section-label">Program Unggulan NIEC Indonesia</span>
          <h2 className="hp-section-title">Dua Jalur Menuju Karier Internasional</h2>
          <p className="hp-section-desc">
            Pilih program yang paling sesuai dengan tujuan dan kondisimu saat ini.
            Keduanya dirancang untuk memberi pengalaman nyata di industri perhotelan dan pariwisata global.
          </p>
        </motion.div>

        {/* Program Cards — stagger left/right alternating */}
        <motion.div
          className="hp-programs-grid"
          variants={staggerContainer(0.2, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
        >
          {programs.map((p, i) => (
            <motion.div
              key={i}
              className={`hp-program-card ${p.theme}`}
              variants={scaleFade}
            >
              <div className="hp-card-top-bar">
                <img src={p.image} alt={p.title} className="img-fluid" />
                <div className="hp-card-eyebrow">
                  <div className="hp-badge">
                    <span className="hp-flag"><FlagIcon code={p.code} size={18} /></span>
                    {p.badge}
                  </div>
                </div>
              </div>

              <div className='p-4' style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* <p className="hp-card-country">{p.country}</p> */}
                <h3 className="hp-card-title">{p.title}</h3>
                <p className="hp-card-desc">{p.desc}</p>

                <ul className="hp-highlights-list">
                  {p.highlights.map((h, j) => (
                    <li key={j} className="hp-highlight-item">
                      <span className="hp-highlight-icon">
                        <i className={h.icon} />
                      </span>
                      <span className="hp-highlight-text">{h.text}</span>
                    </li>
                  ))}
                </ul>

                <div className="hp-card-actions">
                  <Link to={p.link} className="hp-btn-primary">
                    {p.cta} <i className="fa-solid fa-arrow-right" />
                  </Link>
                  {/* <Link to={p.link} className="hp-btn-ghost">
                    {p.ctaSecondary}
                  </Link> */}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <TestimonialsSection />

        <Register />

      </div>
    </section>
  );
};

export default HomePreview;
